import { gql, useMutation } from "@apollo/client";

const UPDATE_DESIGNER_APPLICATION_MUTATION = gql`
  mutation UpdateDesignerApplication($application: UpdateDesignerApplicationInput!) {
    updateDesignerApplication(application: $application)
  }
`;

// Normalize enum fields: convert empty strings to null/undefined before sending to GraphQL
const normalizeEnumFields = (data: any): any => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(item => normalizeEnumFields(item));
  }

  const normalized: any = { ...data };

  // Normalize experience array enum fields
  if (normalized.experience && Array.isArray(normalized.experience)) {
    normalized.experience = normalized.experience.map((exp: any) => {
      const normalizedExp: any = { ...exp };
      // Only convert empty strings to null - valid enum values are preserved
      if (normalizedExp.employmentType === '') {
        console.log('🔧 Normalizing: employmentType empty string -> null');
        normalizedExp.employmentType = null;
      } else if (normalizedExp.employmentType) {
        console.log('✅ Preserving valid employmentType:', normalizedExp.employmentType);
      }
      if (normalizedExp.firmSize === '') {
        console.log('🔧 Normalizing: firmSize empty string -> null');
        normalizedExp.firmSize = null;
      } else if (normalizedExp.firmSize) {
        console.log('✅ Preserving valid firmSize:', normalizedExp.firmSize);
      }
      return normalizedExp;
    });
  }

  // Normalize references array enum fields
  if (normalized.references && Array.isArray(normalized.references)) {
    normalized.references = normalized.references.map((ref: any) => {
      const normalizedRef: any = { ...ref };
      if (normalizedRef.type === '') {
        normalizedRef.type = null;
      }
      if (normalizedRef.ProjectType === '') {
        normalizedRef.ProjectType = null;
      }
      return normalizedRef;
    });
  }

  // Normalize businessGoals enum fields
  if (normalized.businessGoals && typeof normalized.businessGoals === 'object') {
    const goals: any = { ...normalized.businessGoals };
    if (goals.projectCapacity) {
      if (goals.projectCapacity.largeScale === '') {
        goals.projectCapacity.largeScale = null;
      }
      if (goals.projectCapacity.smallScale === '') {
        goals.projectCapacity.smallScale = null;
      }
    }
    normalized.businessGoals = goals;
  }

  // Normalize status enum field
  if (normalized.status === '') {
    normalized.status = null;
  }

  return normalized;
};

export const useUpdateDesignerApplicationMutation = () => {
  const [updateDesignerApplicationMutation, { loading, error, data }] = useMutation(
    UPDATE_DESIGNER_APPLICATION_MUTATION,
    {
      refetchQueries: ['GetAllDesignerAccounts'],
      awaitRefetchQueries: true,
    }
  );

  const updateDesignerApplication = async (application: any) => {
    try {
      // Normalize enum fields before sending to GraphQL
      // This ONLY converts empty strings to null - valid enum values are preserved
      const normalizedApplication = normalizeEnumFields(application);
      
      console.log('📤 Sending normalized application to GraphQL:', {
        experienceCount: normalizedApplication.experience?.length || 0,
        referencesCount: normalizedApplication.references?.length || 0,
      });
      
      const { data } = await updateDesignerApplicationMutation({
        variables: { application: normalizedApplication },
      });
      return data?.updateDesignerApplication;
    } catch (error) {
      console.error("Error updating designer application:", error);
      throw error;
    }
  };

  return { updateDesignerApplication, loading, error, data };
};
